import React, { FormEvent, useState, ChangeEvent, useContext } from 'react'
import { Box, Button, TextField, Typography, styled } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TextSizeContext } from 'data/index'
import { MobileWidth, TextSizes } from 'entities/constants'
import useScreenSize from 'hooks/ScreenSize'
import WithLoginData from 'ui/containers/login'
import { LoginPropType } from 'entities/interfaces'
import PrivacyComponent from 'ui/components/shared/PrivacyComponent'

const LoginForm = styled('form')<{ ismobile?: string }>(({ theme, ismobile }) => ({
    width: '32rem',
    borderRadius: '2rem',
    padding: '3rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'fixed',
    gap: '2rem',
    paddingBottom: '6rem',
    maxHeight: '100%',
    overflowY: "scroll",
    "&::-webkit-scrollbar": {
      width: "0.4rem",
      height: '0.4rem',
    },
    "&::-webkit-scrollbar-track": {
      background: 'transparent',
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.dark,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.grey[700],
    },
}))

const VarticalBox = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
}))

const LoginInput = styled(TextField)(({ theme }) => ({
    '.MuiOutlinedInput-root': {
        padding: '0.25rem 1.25rem',
        width: '23.75rem',
        height: '3.5rem',
        borderRadius: '4rem',
        boxShadow: `0px 2px 4px rgba(0, 0, 0, 0.2)`,
        '& fieldset': {
            borderColor: '#949494',
          },
          '&:hover fieldset': {
            borderColor: '#3A4754',
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
          },
    },
}))

const LoginButton = styled(Button)(({ theme }) => ({
    color: '#374755',
    width: '23.75rem',
    height: '3.5rem',
    borderRadius: '4rem',
    textTransform: 'none',
    display: 'flex',
    gap:'0.75rem'
}))

const LoginButtonText = styled(Typography)(({ theme }) => ({
    color: '#FFF'
}))

const ErrorTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
}))

const Login = ({loginUser, acceptPrivacy}:LoginPropType) => {
    const { t } = useTranslation()
    const navigate = useNavigate();
    const { state: textSize } = useContext(TextSizeContext)

    const [formData, setFormData] = useState({ username: '', password: '' })
    const [error, setError] = useState({ username: false, password: false, message: '' })
    const { width } = useScreenSize()

    const [privacyAccepted, setPrivacyAccepted] = useState(false)
    const [showPrivacyModal, setShowPrivacyModal] = useState(false)
    const [pendingLoginData, setPendingLoginData] = useState<{ token: string, firstName: string, lastName: string } | null>(null)

    const [privacyRequired, setPrivacyRequired] = useState(false)
    const [isFirstLogin, setIsFirstLogin] = useState(false)

    const confirmPrivacyAndLogin = async () => {
        if (!privacyAccepted || !pendingLoginData) return
        const resp = await acceptPrivacy(pendingLoginData.token);
        if (resp.status !== 200) {
          return;
        }
        const { token, firstName, lastName } = pendingLoginData
        window.localStorage.setItem("currentUserToken", token)
        window.localStorage.setItem("currentUserName", `${firstName} ${lastName}`)
        setShowPrivacyModal(false)
        navigate('/')
      }
      
const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  if (!formData.username || !formData.password) {
    setError({
      username: !formData.username,
      password: !formData.password,
      message : t('Username and password required'),
    });
    return;
  }

  const response = await loginUser(formData.username, formData.password);

  if (response.status !== 200) {
    setError({ username: true, password: true, message: response.message });
    return;
  }

  const {
    token, firstName, lastName,
    access_count, privacy_accepted_at,
  } = response.content;

  const firstLogin         = Number(access_count) === 0;
  const privacyNotAccepted = privacy_accepted_at === null;

  if (firstLogin || privacyNotAccepted) {
    setIsFirstLogin(firstLogin);
    setPrivacyRequired(true);
    setPrivacyAccepted(false);
    setPendingLoginData({ token, firstName, lastName });
    setShowPrivacyModal(true);
    return;
  }

  window.localStorage.setItem('currentUserToken', token);
  window.localStorage.setItem('currentUserName', `${firstName} ${lastName}`);
  navigate('/');
};

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, key: string) => {
        setFormData({
            ...formData,
            [key]: event.target.value
        })
        setError({ ...error, [key]: false, message: '' })
    };

    return (
        <>
          <LoginForm onSubmit={handleLogin} ismobile={width <= MobileWidth ? 'true' : undefined}>
            <VarticalBox>
              <Typography component="h1" fontSize={TextSizes[textSize].extraLargeTitle} color="#4A4B4D">
                {t('Login')}
              </Typography>
              <Typography component="h2" fontSize={TextSizes[textSize].subhead} color="#7C7D7E" textAlign="center">
                {t('Enter your login details')}
              </Typography>
            </VarticalBox>
    
            <VarticalBox>
              <VarticalBox gap="1.5rem">
                <LoginInput
                  placeholder={t('Your Email')}
                  type="email"
                  value={formData.username}
                  onChange={(e) => handleChange(e, 'username')}
                  error={error.username}
                  InputProps={{ sx: { fontSize: TextSizes[textSize].subhead } }}
                />
                <LoginInput
                  placeholder={t('Password')}
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange(e, 'password')}
                  error={error.password}
                  InputProps={{ sx: { fontSize: TextSizes[textSize].subhead } }}
                />
              </VarticalBox>
    
              <Box sx={{ height: '1rem' }}>
                {error && <ErrorTypography fontSize={TextSizes[textSize].subhead}>{t(error.message)}</ErrorTypography>}
              </Box>
            </VarticalBox>
    
            <VarticalBox>
              <LoginButton variant="contained" color="primary" type="submit" sx={{ fontSize: TextSizes[textSize].body }}>
                {t('Login')}
              </LoginButton>
              <Typography color="#6D6D6D" sx={{ marginTop: '2rem', cursor: 'pointer', fontSize: TextSizes[textSize].callout }}>
                {t('Forgot your password')}?
              </Typography>
            </VarticalBox>
    
            <VarticalBox>
              <Typography component="p" fontSize={TextSizes[textSize].body} sx={{ textAlign: 'center', marginTop: '1rem' }}>
                {t("Don't have an account")}? <b>{t('Sign Up')}</b>
              </Typography>
            </VarticalBox>
          </LoginForm>
    
          {isFirstLogin && (
            <PrivacyComponent open={showPrivacyModal} onClose={() => {
                if (privacyRequired) {
                  setShowPrivacyModal(false)
                  setPrivacyRequired(false)
                  setPendingLoginData(null)
                } else {
                  setShowPrivacyModal(false)
                }
              }}
              >
                
              <Box mt={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  />
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    Ich habe die Datenschutzerklärung gelesen und akzeptiert.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!privacyAccepted}
                  onClick={confirmPrivacyAndLogin}
                  sx={{ textTransform: 'none' }}
                >
                  Datenschutzerklärung gelesen und akzeptiert
                </Button>
              </Box>
            </PrivacyComponent>
          )}
          {showPrivacyModal  && (
            <PrivacyComponent open={showPrivacyModal} onClose={() => {
           }}
              >
                
              <Box mt={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  />
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    Ich habe die Datenschutzerklärung gelesen und akzeptiert.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!privacyAccepted}
                  onClick={confirmPrivacyAndLogin}
                  sx={{ textTransform: 'none' }}
                >
                  Datenschutzerklärung gelesen und akzeptiert
                </Button>
              </Box>
            </PrivacyComponent>
          )}
          </>
    )
    }
export default WithLoginData(Login)